function renderNeedData(questionRaws: any, questionService: any, questionControlService: any) {
  const questionaire = questionService.buildNeedsQuestionaire(questionRaws);
  const form = questionControlService.toFormGroup(questionaire);

  return { questionaire, form };
}

function getValueWithUnit(unit: string, value: string) {
  let result: string;

  switch (unit) {
    case '$':
      result = `$${value}`;
      break;
    case '%':
      result = `${value}%`;
      break;
    case 'year':
      result = `${value} year`;
      break;
    default:
      result = value;
  }

  return result;
}

function getAgeFromDOB(dob: string) {
  let currentAge = 0;
  const dateOfDOB = new Date(dob);
  const currentDate = new Date();

  if (dob && dateOfDOB.getFullYear() < currentDate.getFullYear()) {
    if (dateOfDOB.getMonth() + 1 < currentDate.getMonth() + 1) {
      currentAge = currentDate.getFullYear() - dateOfDOB.getFullYear();
    } else if (dateOfDOB.getMonth() + 1 > currentDate.getMonth() + 1) {
      currentAge = currentDate.getFullYear() - dateOfDOB.getFullYear() - 1;
    } else {
      if (dateOfDOB.getDate() <= currentDate.getDate()) {
        currentAge = currentDate.getFullYear() - dateOfDOB.getFullYear();
      } else if (dateOfDOB.getDate() > currentDate.getDate()) {
        currentAge = currentDate.getFullYear() - dateOfDOB.getFullYear() - 1;
      }
    }
  } else {
    currentAge = 0;
  }

  return Number(currentAge);
}

/****************My Needs - Capital needs Funcs*****************/
function PMT(rate: number, nper: number, pv: number, fv: number, type: number = 0) {
  // định nghĩa hàm PMT tại https://support.office.com/vi-vn/article/pmt-ha%CC%80m-pmt-0214da64-9a63-4996-bc20-214433fa6441
  if (rate !== 0.0) {
    const q = Math.pow(1 + rate, nper);
    return -((rate * (-fv + q * pv)) / ((-1 + q) * (1 + rate * type))
    );
  } else if (nper !== 0.0) {
    return -((-fv + pv) / nper);
  }

  return 0;
}

function getCapitalNeedToSave(
  finaceSpecialProject: number,
  financeCapital: number,
  currentSaving: number,
  startingFinace: number,
  investmentRates: number,
  inflationRates: number
) {
  let capital = 0;
  const rate = (investmentRates - inflationRates) / 12;
  const nper = (finaceSpecialProject - startingFinace) * 12;

  capital = PMT(rate, nper, currentSaving, financeCapital);

  return Math.round(capital);
}

/****************My Needs - Retirement Funcs*****************/
function getAmountRetirementNeedToSave(
  investmentRates: number,
  inflationRates: number,
  moneyMonthlyReceived: number,
  yearsToReceived: number
) {
  let amount = 0, buffer = 0, i, j;

  for (i = 1; i <= yearsToReceived; i++) {
    for (j = 1; j <= 12; j++) {
      if (i === 1 && j === 1) {
        amount += moneyMonthlyReceived;
        buffer = moneyMonthlyReceived;
      } else {
        buffer = (buffer * (1 + inflationRates / 12)) / (1 + investmentRates / 12);
        amount += buffer;
      }
    }
  }

  return Math.round(amount);
}

function getAmountMonthlyNeedToSaveForRetirement(
  yearRetirement: number,
  timeReceivedIncome: number,
  receiveEveryMonth: number,
  investmentRates: number,
  inflationRates: number,
  startSavingIn: number,
  userAge: number
) {
  const rate = (investmentRates - inflationRates) / 12;
  const nper = (yearRetirement - userAge - startSavingIn) * 12;
  const amount = getAmountRetirementNeedToSave(
    investmentRates,
    inflationRates,
    receiveEveryMonth,
    timeReceivedIncome
  );

  return Math.round(PMT(rate, nper, 0, amount));
}

/****************My Needs - Income Protection Funcs*****************/
function generateRangeYears(minYear: number, maxYear: number) {
  const result = [];
  if (maxYear - minYear >= 0) {
    let i = 0;
    while (i <= maxYear - minYear) {
      result.push(minYear + i);
      i++;
    }
  }

  return result;
}

function generateRangeValues(rate: number, expenses: number, rangeYear: number) {
  const result = [];

  for (let i = 0; i <= rangeYear - 1; i++) {
    result.push(Number((expenses * Math.pow(1 + rate, i)).toFixed(2)));
  }

  return result;
}

function generateDataChartForIncomeProtection(
  data: {
    endOfMonthlyLoan: number;
    endOfOtherInstallmentCommitment: number;
    incomeFlowEvolution: string;
    inflationRates: number;
    monthlyLoanCost: number;
    myFamilyMonthlyExpenses: number;
    myMonthlyExpenses: number;
    otherInstallmentCommitment: number;
    yearNotNeedSupport: number;
  },
  userAge: number
) {
  const monthlyExpenses = new RangeAreaChartPart();
  const familyExpenses = new RangeAreaChartPart();
  const loan = new RangeAreaChartPart();
  const ortherLoan = new RangeAreaChartPart();

  // debugger
  const monthlyExpensesData = generateRangeValues(
    Number(data.inflationRates),
    Number(data.myMonthlyExpenses),
    generateRangeYears(userAge, 100).length
  );
  if (monthlyExpensesData.length > 0) {
    monthlyExpensesData.forEach((item, index) => {
      monthlyExpenses.data.push({
        month: String(userAge + index),
        min: 0,
        max: item
      });
    });
  } else {
    monthlyExpenses.data = [];
  }

  const familyExpensesData = generateRangeValues(
    Number(data.inflationRates),
    Number(data.myFamilyMonthlyExpenses),
    generateRangeYears(userAge, Number(data.yearNotNeedSupport)).length
  );

  if (familyExpensesData.length > 0) {
    familyExpensesData.forEach((item, index) => {
      familyExpenses.data.push({
        month: String(userAge + index),
        min: monthlyExpensesData.length > 0 && monthlyExpensesData[index] ? monthlyExpensesData[index] : 0,
        max: monthlyExpensesData.length > 0 && monthlyExpensesData[index] ? item + monthlyExpensesData[index] : item
      });
    });
    const lastestData = familyExpenses.data.pop();
    familyExpenses.data.push({
      month: lastestData.month,
      min: lastestData.min,
      max: lastestData.min
    });
  } else {
    familyExpenses.data = [];
  }

  const loanData = generateRangeValues(
    0,
    Number(data.monthlyLoanCost),
    generateRangeYears(userAge, userAge + Number(data.endOfMonthlyLoan)).length
  );

  if (loanData.length > 0) {
    loanData.forEach((item, index) => {
      loan.data.push({
        month: String(userAge + index),
        min: familyExpenses.data.length > 0 && familyExpenses.data[index]
          ? familyExpenses.data[index].max
          : monthlyExpenses.data.length > 0 && monthlyExpenses.data[index]
            ? monthlyExpenses.data[index].max
            : 0,
        max: familyExpenses.data.length > 0 && familyExpenses.data[index]
          ? item + familyExpenses.data[index].max
          : monthlyExpenses.data.length > 0 && monthlyExpenses.data[index]
            ? item + monthlyExpenses.data[index].max
            : item
      });
    });
    const lastestData = loan.data.pop();
    loan.data.push({
      month: lastestData.month,
      min: lastestData.min,
      max: lastestData.min
    });
  } else {
    loan.data = [];
  }

  const ortherLoanData = generateRangeValues(
    0,
    Number(data.otherInstallmentCommitment),
    generateRangeYears(userAge, userAge + Number(data.endOfOtherInstallmentCommitment)).length
  );
  if (ortherLoanData.length > 0) {
    ortherLoanData.forEach((item, index) => {
      ortherLoan.data.push({
        month: String(userAge + index),
        min: loan.data.length > 0 && loan.data[index]
          ? loan.data[index].max
          : familyExpenses.data.length > 0 && familyExpenses.data[index]
            ? familyExpenses.data[index].max
            : monthlyExpenses.data.length > 0 && monthlyExpenses.data[index]
              ? monthlyExpenses.data[index].max
              : 0,
        max: loan.data.length > 0 && loan.data[index]
          ? item + loan.data[index].max
          : familyExpenses.data.length > 0 && familyExpenses.data[index]
            ? item + familyExpenses.data[index].max
            : monthlyExpenses.data.length > 0 && monthlyExpenses.data[index]
              ? item + monthlyExpenses.data[index].max
              : item,
      });
    });
    const lastestData = ortherLoan.data.pop();
    ortherLoan.data.push({
      month: lastestData.month,
      min: lastestData.min,
      max: lastestData.min
    });
  } else {
    ortherLoan.data = [];
  }

  // add name - color
  monthlyExpenses.name = 'Monthly Expenses';
  monthlyExpenses.color = '#a2a2a2';
  familyExpenses.name = 'Family Expenses';
  familyExpenses.color = '#ffc72a';
  loan.name = 'Loan';
  loan.color = '#3c6ec8';
  ortherLoan.name = 'Other Loan';
  ortherLoan.color = '#f38543';

  return {
    monthlyExpenses,
    familyExpenses,
    loan,
    ortherLoan
  };
}

/**************** My Needs - Regular savings Funcs*****************/
function getAmountOfRegularSavings(
  yearlyIncome: number,
  savingPercent: number,
  currentSaving: number,
  otherSaving: number,
  timeSavings: number,
  investmentRates: number,
  inflationRates: number
) {
  const firstYearSavings = (currentSaving + otherSaving) *
    Math.pow(1 + (investmentRates - inflationRates), 0) +
    yearlyIncome * savingPercent;

  if (timeSavings === 0) {
    return firstYearSavings;
  }

  let result = 0, buffer = 0, i = 0;
  while (i <= timeSavings) {
    if (i === 0) {
      result = firstYearSavings;
      buffer = firstYearSavings;
    } else {
      result = buffer * (1 + investmentRates - inflationRates) + yearlyIncome * savingPercent;
      buffer = result;
    }
    i++;
  }

  return Math.round(result);
}

function getAmountMonthlyIncomeOfRegularSavings(
  yearlyIncome: number,
  savingPercent: number,
  currentSaving: number,
  otherSaving: number,
  timeSavings: number,
  timeSavingForMonthlyIncome: number,
  investmentRates: number,
  inflationRates: number
) {
  const rate = (investmentRates - inflationRates) / 12;
  const nper = timeSavingForMonthlyIncome * 12;
  const amount = getAmountOfRegularSavings(
    yearlyIncome,
    savingPercent,
    currentSaving,
    otherSaving,
    timeSavings - 1,
    investmentRates,
    inflationRates
  );
  const result = -Math.round(PMT(rate, nper, amount, 0));
  if (isNaN(result) || !isFinite(result)) return 0;
  return result;
}

class RangeAreaChartPart {
  name: string;
  color: string;
  data: Array<{
    month: string,
    min: number,
    max: number
  }>;

  constructor() {
    this.name = '';
    this.color = '';
    this.data = new Array<{
      month: '0',
      min: 0,
      max: 0
    }>();
  }
}

function generateDataChartForMySaving(
  data: {
    currentSavings: number;
    inflationRates: number;
    investmentRates: number;
    monthlyIncome: number;
    myYearlyIncome: number;
    otherSavings: number;
    savingPercentForYearlyIncome: number;
    timeSavings: number;
  },
  userAge: number
) {
  const defaultMinRange = 18; // min range of chart
  const defaultMaxRange = 99; // max length of chart

  const startEmpty = new RangeAreaChartPart();
  const otherSavings = new RangeAreaChartPart();
  const currentSavings = new RangeAreaChartPart();
  const amountReceived = new RangeAreaChartPart();
  const yearlyIncome = new RangeAreaChartPart();
  const endEmpty = new RangeAreaChartPart();

  if (userAge < defaultMinRange || userAge > defaultMaxRange) {
    return {
      userAge,
      startEmpty,
      otherSavings,
      currentSavings,
      amountReceived,
      yearlyIncome,
      endEmpty
    };
  }

  let minSavings = 0, maxSavings = 0, minMonthlyIncome = 0, maxMonthlyIncome = 0;

  // render startEmpty
  if (userAge < defaultMinRange) {
    minSavings = defaultMinRange;
    startEmpty.data = [];
  } else {
    minSavings = userAge;
    for (let i = defaultMinRange; i <= userAge; i++) {
      startEmpty.data.push({ month: String(i), min: 0, max: 0 });
    }
  }

  if (userAge + data.timeSavings > defaultMaxRange) {
    maxSavings = defaultMaxRange;
  } else {
    maxSavings = userAge + data.timeSavings - 1;
    minMonthlyIncome = maxSavings;

    if (userAge + data.timeSavings + data.monthlyIncome > defaultMaxRange) {
      maxMonthlyIncome = defaultMaxRange;
    } else {
      maxMonthlyIncome = userAge + data.timeSavings + data.monthlyIncome - 1;
    }
  }

  // render ortherSavings , currentSavings
  let maxOtherSavings = 0;
  for (let i = minSavings; i <= maxSavings; i++) {
    if (i === minSavings) {
      maxOtherSavings = Number(data.otherSavings);
    } else {
      maxOtherSavings = Math.round(otherSavings.data[otherSavings.data.length - 1].max * (1 + data.investmentRates - data.inflationRates));
    }

    otherSavings.data.push({
      month: String(i),
      min: 0,
      max: maxOtherSavings
    });
    currentSavings.data.push({
      month: String(i),
      min: maxOtherSavings,
      max:
        i === minSavings
          ?
          Number(data.otherSavings + data.currentSavings)
          :
          Math.round(currentSavings.data[currentSavings.data.length - 1].max * (1 + data.investmentRates - data.inflationRates))
    });
  }

  // render amountReceived
  let j = 0, maxAmountReceived = 0;
  for (let i = minSavings; i <= maxSavings; i++) {
    amountReceived.data.push({
      month: String(i),
      min: 0,
      max: getAmountOfRegularSavings(
        data.myYearlyIncome,
        data.savingPercentForYearlyIncome,
        data.currentSavings,
        data.otherSavings,
        j,
        data.investmentRates,
        data.inflationRates
      )
    });
    if (i === maxSavings) {
      maxAmountReceived = amountReceived.data[amountReceived.data.length - 1].max;
    }
    j++;
  }

  // render monthlyIncome
  if (userAge + data.timeSavings > defaultMaxRange) {
    yearlyIncome.data = [];
  } else {
    let yearNumber = 0;
    for (let i = minMonthlyIncome; i <= maxMonthlyIncome; i++) {
      yearlyIncome.data.push({
        month: String(i),
        min: 0,
        max:
          yearNumber === 0
            ?
            maxAmountReceived
            :
            Math.round(
              (
                yearlyIncome.data[yearlyIncome.data.length - 1].max +
                PMT(
                  data.investmentRates - data.inflationRates,
                  data.monthlyIncome - yearNumber + 1,
                  yearlyIncome.data[yearlyIncome.data.length - 1].max, 0, 1
                )
              ) * (
                1 + (data.investmentRates - data.inflationRates)
              )
            )
      });
      yearNumber++;
    }
  }

  // render endEmpty
  if (userAge + data.timeSavings + data.monthlyIncome > defaultMaxRange) {
    endEmpty.data = [];
  } else {
    for (let i = maxMonthlyIncome; i <= defaultMaxRange; i++) {
      endEmpty.data.push({
        month: String(i),
        min: 0,
        max: 0
      });
    }
  }

  // add name - color
  otherSavings.name = 'Evolution of the value of my initial other savings';
  otherSavings.color = '#0398f4';
  currentSavings.name = 'Evolution of the value of my initial savings';
  currentSavings.color = '#29ccb6';
  amountReceived.name = 'Evolution of the savings created from my income';
  amountReceived.color = '#fbae17';
  yearlyIncome.name = 'Evolution of my total savings from the day I stop saving';
  yearlyIncome.color = '#ef7406';

  return {
    userAge,
    startEmpty,
    otherSavings,
    currentSavings,
    amountReceived,
    yearlyIncome,
    endEmpty
  };
}

export {
  renderNeedData,
  getValueWithUnit,
  getAgeFromDOB,
  getCapitalNeedToSave,
  getAmountRetirementNeedToSave,
  getAmountMonthlyNeedToSaveForRetirement,
  getAmountOfRegularSavings,
  getAmountMonthlyIncomeOfRegularSavings,
  generateRangeYears,
  generateRangeValues,
  generateDataChartForIncomeProtection,
  generateDataChartForMySaving
};



// WEBPACK FOOTER //
// ./src/app/features/my-needs/my-needs.ts